import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useBulkStockUpload } from '../useBulkStockUpload';
import { AdjustmentReferenceField } from '../../AdjustmentReferenceField';

interface PreviewStepProps {
  onSubmit: () => void;
  /**
   * BLU-669: disables the submit button (with a reason line) while the
   * cross-org target picker is unset — "required" on the picker was
   * previously decorative here and a 1000-row CSV could submit with no
   * target at all.
   */
  submitDisabledReason?: string | null;
}

const PreviewStep: React.FC<PreviewStepProps> = ({ onSubmit, submitDisabledReason }) => {
  const { previewRows, reference, setReference, setStep, setValidationErrors } = useBulkStockUpload();
  const { format } = useCurrencyFormatter();

  const summary = useMemo(() => {
    let totalUnits = 0;
    let totalCost = 0;
    let hasCostData = false;

    for (const row of previewRows) {
      totalUnits += row.qtyToAdd;
      if (row.totalCostImpact != null) {
        totalCost += row.totalCostImpact;
        hasCostData = true;
      }
    }

    return {
      totalUnits,
      skuCount: previewRows.length,
      totalCost,
      hasCostData,
    };
  }, [previewRows]);

  const handleChangeFile = () => {
    setValidationErrors([]);
    setStep('upload');
  };

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-2 text-sm px-1 flex-wrap">
        <span>
          Adding <strong>{summary.totalUnits.toLocaleString()}</strong> units
          to <strong>{summary.skuCount}</strong> SKU
          {summary.skuCount !== 1 ? 's' : ''}
        </span>
        {summary.hasCostData && (
          <>
            <span className="text-muted-foreground">|</span>
            <span>
              Est. value: <strong>{format(summary.totalCost)}</strong>
            </span>
          </>
        )}
      </div>

      {/* Preview table */}
      <div className="border rounded-md max-h-[340px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                SKU
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                Current
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                +Qty
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                New Total
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground hidden sm:table-cell">
                Unit Cost
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground hidden sm:table-cell">
                Cost Impact
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {previewRows.map((row) => (
              <tr key={row.sku} className="hover:bg-muted/50">
                <td className="px-3 py-1.5 font-mono text-xs">
                  <span className="flex items-center gap-1.5">
                    {row.sku}
                    {row.isNewAtLocation && (
                      <Badge
                        variant="default"
                        size="sm"
                        className="bg-threadheads-purple/10 text-threadheads-purple border-threadheads-purple/20"
                      >
                        New at location
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs">
                  {row.isNewAtLocation ? (
                    <span className="text-muted-foreground">--</span>
                  ) : (
                    row.currentStock
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-green-600 font-medium">
                  +{row.qtyToAdd}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs font-medium">
                  {row.newTotal}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs hidden sm:table-cell text-muted-foreground">
                  {row.unitCost != null ? format(row.unitCost) : '--'}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs hidden sm:table-cell">
                  {row.totalCostImpact != null ? (
                    <span className="text-green-600">
                      +{format(row.totalCostImpact)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Operator reference / order number — stored on the adjustment-
          document header so the whole import is reconcilable as one doc. */}
      <div className="sm:max-w-xs">
        <AdjustmentReferenceField
          id="bulk-upload-reference"
          value={reference}
          onChange={setReference}
          labelClassName="text-xs"
        />
      </div>

      {/* Actions */}
      <div className="flex flex-col items-end gap-1 pt-2">
        {submitDisabledReason && (
          <p className="text-xs text-amber-700">{submitDisabledReason}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleChangeFile}>
            Change File
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!!submitDisabledReason}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            Add Stock to {previewRows.length} Item{previewRows.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreviewStep;
